import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import styled from "styled-components";
import { CaretRightIcon, XIcon } from "@phosphor-icons/react";
import { OrganizationContext } from "../contexts/OrganizationContext";

export default function CreateAccountCustom(props) {
  const { organizations } = useContext(OrganizationContext);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    organizationName: "",
    role: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [step, setStep] = useState(1); // 1: Email verification, 2: Form completion
  const [userExists, setUserExists] = useState(null); // null: not checked, true: exists, false: doesn't exist
  const [isVerifying, setIsVerifying] = useState(false);

  // Search states for dropdowns
  const [orgSearch, setOrgSearch] = useState("");
  const [roleSearch, setRoleSearch] = useState("");
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);

  // Refs for closing dropdowns when clicking outside
  const orgDropdownRef = useRef(null);
  const roleDropdownRef = useRef(null);
  const orgSearchRef = useRef(null);
  const roleSearchRef = useRef(null);

  const [roleOptions, setRoleOptions] = useState([]);

  useEffect(() => {
    if (props.showLock) {
      handleSignUp();
      props.setShowLock(false);
    }
  }, [props.showLock]);

    const normalizeOptions = (list = []) =>
        (Array.isArray(list) ? list : [])
            .sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0))
            .map((item) => ({
                value: item.Value,
                label: item.Label ?? item.Value,
            }));
    // Load role  options from a single API call 
    useEffect(() => {
        if (!showModal) return;

        const fetchRoleAndSkillOptions = async () => {
            try {

                const apiUrl =
                    process.env.REACT_APP_API_URL + "/master-data/roles-skills?tag=admin";

                const response = await fetch(apiUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: process.env.REACT_APP_API_AUTHORIZATION,
                        Accept: "application/json",
                    },
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || "Failed to fetch roles/skills");
                }

                const roles = normalizeOptions(data?.data?.roles);


                setRoleOptions(roles);
            } catch (err) {
                console.error("Fetch error:", err);

                setRoleOptions([{ value: "0", label: "---No Records---" }]);
            }
        };

        fetchRoleAndSkillOptions();
    }, [showModal]);

  // ESC key to close modal
  useEffect(() => {
    if (showModal) {
      const handleEscKey = (e) => {
        if (e.key === "Escape") handleCloseModal();
      };
      window.addEventListener("keydown", handleEscKey);
      return () => window.removeEventListener("keydown", handleEscKey);
    }
  }, [showModal]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(event.target) &&
        orgSearchRef.current &&
        !orgSearchRef.current.contains(event.target)
      ) {
        setShowOrgDropdown(false);
        setOrgSearch("");
      }
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target) &&
        roleSearchRef.current &&
        !roleSearchRef.current.contains(event.target)
      ) {
        setShowRoleDropdown(false);
        setRoleSearch("");
      }
    };

    if (showOrgDropdown || showRoleDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOrgDropdown, showRoleDropdown]);

  const handleSignUp = () => {
    setShowModal(true);
  };

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setFormData({
      email: "",
      password: "",
      fullName: "",
      organizationName: "",
      role: "",
    });
    setErrors({});
    setIsSubmitting(false);
    setOrgSearch("");
    setRoleSearch("");
    setShowOrgDropdown(false);
    setShowRoleDropdown(false);
    setApiResponse(null);
    setStep(1);
    setUserExists(null);
    setIsVerifying(false);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleOrgSelect = (orgName) => {
    setFormData((prev) => ({ ...prev, organizationName: orgName }));
    setOrgSearch("");
    setShowOrgDropdown(false);
    if (errors.organizationName) {
      setErrors((prev) => ({
        ...prev,
        organizationName: "",
      }));
    }
  };

  const handleRoleSelect = (roleValue) => {
    setFormData((prev) => ({ ...prev, role: roleValue }));
    setRoleSearch("");
    setShowRoleDropdown(false);
    if (errors.role) {
      setErrors((prev) => ({
        ...prev,
        role: "",
      }));
    }
  };

  const filteredOrganizations =
    organizations?.filter((org) =>
      org.name.toLowerCase().includes(orgSearch.toLowerCase()),
    ) || [];

  const filteredRoles = roleOptions.filter((role) =>
    role.label.toLowerCase().includes(roleSearch.toLowerCase()),
  );

  const validateEmail = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation (already verified in step 1)
    if (!formData.email) {
      newErrors.email = "Email is required";
    }

    // Only validate password if user doesn't exist
    if (!userExists) {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (formData.password.length < 6) {
        newErrors.password = "Password must be at least 6 characters";
      }
    }

    if (!formData.fullName) {
      newErrors.fullName = "Full name is required";
    } else if (formData.fullName.length < 4) {
      newErrors.fullName = "Full name must be at least 4 characters";
    }

    if (!formData.organizationName) {
      newErrors.organizationName = "Please select an organization";
    }

    if (!formData.role) {
      newErrors.role = "Please select a role";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVerifyEmail = async () => {
    if (!validateEmail()) return;

    setIsVerifying(true);
    setApiResponse(null);

    try {
      console.log("Verifying email:", formData.email);

      // Call the user exists API
      const apiUrl = `${process.env.REACT_APP_API_URL || ""}/auth0/user/exists`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: formData.email }),
      });

      const responseData = await response.json();

      console.log("User exists API Response:", responseData);

      if (!response.ok) {
        throw new Error(responseData.message || "API request failed");
      }

      // Assuming API returns { exists: true/false }
      const exists = responseData.exists || false;
      setUserExists(exists);

      // Move to step 2
      setStep(2);
    } catch (error) {
      console.error("Email verification failed:", error);
      setApiResponse({
        success: false,
        message: error.message || "Failed to verify email. Please try again.",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleBackToEmail = () => {
    setStep(1);
    setUserExists(null);
    setApiResponse(null);
    // Keep the email but reset other fields
    setFormData((prev) => ({
      email: prev.email,
      password: "",
      fullName: "",
      organizationName: "",
      role: "",
    }));
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    setApiResponse(null);

    try {
      console.log("Form submitted:", formData);

      // Prepare API request body
      const requestBody = {
        email: formData.email,
        name: formData.fullName,
        password: formData.password || undefined, // Only include password if provided
        metadata: {
          role: formData.role,
          fullName: formData.fullName,
          organizationName: formData.organizationName,
        },
      };

      // Remove password field if it's empty (user already exists)
      if (!requestBody.password) {
        delete requestBody.password;
      }

      console.log("API Request:", requestBody);

      // Call the upsert API
      const apiUrl = `${process.env.REACT_APP_API_URL || ""}/auth0/user/upsert`;
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseData = await response.json();

      console.log("API Response:", responseData);

      if (!response.ok) {
        throw new Error(responseData.message || "API request failed");
      }

      setApiResponse({
        success: true,
        message: responseData.message || "Account updated successfully!",
        data: responseData,
      });

      // If there's a custom onSubmit prop, call it
      if (props.onSubmit) {
        await props.onSubmit(formData, responseData);
      }

      // Reset form after successful submission
      setFormData({
        email: "",
        password: "",
        fullName: "",
        organizationName: "",
        role: "",
      });

      // Reset to step 1 after 3 seconds
      setTimeout(() => {
        if (props.onSuccess) {
          props.onSuccess();
        }
        handleCloseModal();
      }, 3000);
    } catch (error) {
      console.error("Signup failed:", error);

      setApiResponse({
        success: false,
        message:
          error.message || "Failed to create/update account. Please try again.",
      });

      setErrors((prev) => ({
        ...prev,
        submit:
          error.message || "Failed to create/update account. Please try again.",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showModal) return null;

  return (
    <ModalOverlay onClick={handleCloseModal}>
      <ModalContainer
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-account-title"
      >
        <ModalHeader>
          <Logo
            src="https://brass-lynx-1229.twil.io/assets/AnyonePray%20Logo-Dark.png"
            alt="AnyonePray Logo"
          />
          <CloseButton
            onClick={handleCloseModal}
            aria-label="Close signup modal"
          >
            <XIcon size={24} />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          <Title id="create-account-title">
            {step === 1 ? "Create Account" : "Create Account"}
          </Title>

          {/* API Response Messages */}
          {apiResponse && (
            <ApiResponseMessage success={apiResponse.success}>
              {apiResponse.message}
            </ApiResponseMessage>
          )}

          {errors.submit && !apiResponse && (
            <SubmitError>{errors.submit}</SubmitError>
          )}

          {/* Step 1: Email Verification */}
          {step === 1 && (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                handleVerifyEmail();
              }}
              noValidate
            >
              <StepDescription>
                Enter your email address to check if you already have an
                account.
              </StepDescription>

              <FormGroup>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter email address"
                  error={errors.email}
                  aria-describedby={errors.email ? "email-error" : undefined}
                  aria-invalid={!!errors.email}
                  autoComplete="email"
                  disabled={isVerifying}
                />
                {errors.email && (
                  <ErrorText id="email-error">{errors.email}</ErrorText>
                )}
              </FormGroup>

              <VerifyButton
                type="button"
                onClick={handleVerifyEmail}
                disabled={isVerifying}
                aria-busy={isVerifying}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {isVerifying ? "Verifying..." : "Verify Email"}
                {!isVerifying && <CaretRightIcon size={24} />}
              </VerifyButton>
            </Form>
          )}

          {/* Step 2: Complete Form */}
          {step === 2 && (
            <Form onSubmit={handleSubmit} noValidate>
              {/* Email display (read-only) */}
              <FormGroup>
                <Label>Email Address</Label>
                <EmailDisplay>
                  <EmailText>{formData.email}</EmailText>
                  <EmailStatus userExists={userExists}>
                    {userExists ? "Existing User" : "New User"}
                  </EmailStatus>
                </EmailDisplay>
                <BackLink onClick={handleBackToEmail}>Change email</BackLink>
              </FormGroup>

              {/* Password field - only show if user doesn't exist */}
              {!userExists && (
                <FormGroup>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Create a password (min. 6 characters)"
                    error={errors.password}
                    aria-describedby={
                      errors.password ? "password-error" : undefined
                    }
                    aria-invalid={!!errors.password}
                    autoComplete="new-password"
                    disabled={isSubmitting || apiResponse?.success}
                  />
                  {errors.password && (
                    <ErrorText id="password-error">{errors.password}</ErrorText>
                  )}
                </FormGroup>
              )}

              <FormGroup>
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  error={errors.fullName}
                  aria-describedby={
                    errors.fullName ? "fullName-error" : undefined
                  }
                  aria-invalid={!!errors.fullName}
                  autoComplete="name"
                  disabled={isSubmitting || apiResponse?.success}
                />
                {errors.fullName && (
                  <ErrorText id="fullName-error">{errors.fullName}</ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="organizationName">Organization</Label>
                <DropdownContainer>
                  <SearchableSelect
                    ref={orgSearchRef}
                    type="text"
                    value={
                      showOrgDropdown
                        ? orgSearch
                        : formData.organizationName || ""
                    }
                    onChange={(e) => {
                      setOrgSearch(e.target.value);
                      if (!showOrgDropdown) setShowOrgDropdown(true);
                    }}
                    onClick={() => setShowOrgDropdown(true)}
                    placeholder="Select or search organization"
                    error={errors.organizationName}
                    readOnly={
                      !showOrgDropdown || isSubmitting || apiResponse?.success
                    }
                    isOpen={showOrgDropdown}
                    disabled={isSubmitting || apiResponse?.success}
                  />
                  <DropdownIcon isOpen={showOrgDropdown}>▼</DropdownIcon>
                  {showOrgDropdown && (
                    <DropdownMenu ref={orgDropdownRef}>
                      <OptionsList>
                        {filteredOrganizations.length > 0 ? (
                          filteredOrganizations.map((org, i) => (
                            <OptionItem
                              key={i}
                              onClick={() => handleOrgSelect(org.name)}
                              selected={formData.organizationName === org.name}
                            >
                              {org.name}
                            </OptionItem>
                          ))
                        ) : (
                          <>
                          <NoResults>No organizations found</NoResults>
                       
                        <OptionItem
                          onClick={() => handleOrgSelect("crossroads")}
                          selected={formData.organizationName === "crossroads"}
                        >
                          Crossroads
                        </OptionItem>
                        </>)}
                      </OptionsList> 
                    </DropdownMenu>
                  )}
                </DropdownContainer>
                {errors.organizationName && (
                  <ErrorText id="org-error">
                    {errors.organizationName}
                  </ErrorText>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="role">Account Role</Label>
                <DropdownContainer>
                  <SearchableSelect
                    ref={roleSearchRef}
                    type="text"
                    value={
                      showRoleDropdown
                        ? roleSearch
                        : roleOptions.find((r) => r.value === formData.role)
                            ?.label || ""
                    }
                    onChange={(e) => {
                      setRoleSearch(e.target.value);
                      if (!showRoleDropdown) setShowRoleDropdown(true);
                    }}
                    onClick={() => setShowRoleDropdown(true)}
                    placeholder="Select or search role"
                    error={errors.role}
                    readOnly={
                      !showRoleDropdown || isSubmitting || apiResponse?.success
                    }
                    isOpen={showRoleDropdown}
                    disabled={isSubmitting || apiResponse?.success}
                  />
                  <DropdownIcon isOpen={showRoleDropdown}>▼</DropdownIcon>
                  {showRoleDropdown && (
                    <DropdownMenu ref={roleDropdownRef}>
                      <OptionsList>
                        {filteredRoles.length > 0 ? (
                          filteredRoles.map((role, i) => (
                            <OptionItem
                              key={i}
                              onClick={() => handleRoleSelect(role.value)}
                              selected={formData.role === role.value}
                            >
                              {role.label}
                            </OptionItem>
                          ))
                        ) : (
                          <NoResults>No roles found</NoResults>
                        )}
                      </OptionsList>
                    </DropdownMenu>
                  )}
                </DropdownContainer>
                {errors.role && (
                  <ErrorText id="role-error">{errors.role}</ErrorText>
                )}
              </FormGroup>

              <Policy>
                By signing up, you agree to our terms of service and privacy
                policy.
              </Policy>

              <SubmitButton
                type="submit"
                disabled={isSubmitting || apiResponse?.success}
                aria-busy={isSubmitting}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {isSubmitting
                  ? "Submitting..."
                  : apiResponse?.success
                    ? userExists
                      ? "Account Updated!"
                      : "Account Created!"
                    : userExists
                      ? "Update Account"
                      : "Create New Account"}
                {!apiResponse?.success && <CaretRightIcon size={24} />}
              </SubmitButton>
            </Form>
          )}
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
}

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 430px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &:focus {
    outline: none;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: #eaeaea;
  position: relative;
`;

const Logo = styled.img`
  height: 40px;
  width: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    color: #333;
    background-color: rgba(0, 0, 0, 0.05);
  }

  &:focus {
    outline: 2px solid #2db3c0;
    outline-offset: 2px;
  }
`;

const ModalContent = styled.div`
  padding: 20px;
`;

const Title = styled.h2`
  margin-bottom: 20px;
  text-align: center;
  font-size: 24px;
  color: #ec3310;
  font-family: Recoleta;
  text-decoration: underline;
`;

const StepDescription = styled.p`
  margin-bottom: 20px;
  text-align: center;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
`;

const ProgressIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  gap: 8px;
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  opacity: ${(props) => (props.active ? "1" : "0.5")};
`;

const StepNumber = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${(props) => (props.active ? "#2db3c0" : "#ddd")};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
`;

const StepLabel = styled.div`
  font-size: 12px;
  color: #666;
  white-space: nowrap;
`;

const ProgressLine = styled.div`
  width: 40px;
  height: 2px;
  background-color: #2db3c0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Label = styled.label`
  margin-bottom: 6px;
  font-weight: 500;
  color: #555;
  font-size: 14px;
  font-family: "Futura PT", sans-serif;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid ${(props) => (props.error ? "#e74c3c" : "#ddd")};
  border-radius: 4px;
  font-size: 16px;
  transition: all 0.2s;
  font-family: "Futura PT", sans-serif;

  &:focus {
    outline: none;
    border-color: #2db3c0;
    box-shadow: 0 0 0 3px rgba(45, 179, 192, 0.2);
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #999;
  }
`;

const EmailDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border: 1px solid #2db3c0;
  border-radius: 4px;
  background-color: #e6f7f9;
`;

const EmailText = styled.span`
  font-weight: 500;
  color: #333;
`;

const EmailStatus = styled.span`
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  background-color: ${(props) => (props.userExists ? "#ffeaa7" : "#a5d6a7")};
  color: ${(props) => (props.userExists ? "#e17055" : "#2e7d32")};
  font-weight: 500;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #2db3c0;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
  padding: 4px 0;
  align-self: flex-start;
  margin-top: 4px;

  &:hover {
    color: #249ba6;
  }
`;

const VerifyButton = styled.button`
  background-color: #2db3c0;
  color: white;
  border: none;
  padding: 14px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 10px;
  font-family: "Space Grotesk", sans-serif;
  letter-spacing: 0.15em;
  display: flex;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #249ba6;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    opacity: 0.7;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(45, 179, 192, 0.4);
  }
`;

const SubmitButton = styled(VerifyButton)`
  // Inherits all styles from VerifyButton
`;

// Enhanced dropdown components
const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchableSelect = styled(Input)`
  width: 100%;
  cursor: pointer;
  background-color: white;
  position: relative;
  z-index: 2;
  padding-right: 36px;
  user-select: none;

  &[readonly] {
    cursor: pointer;
    background-color: white;
  }

  &:focus {
    border-color: ${(props) => (props.isOpen ? "#2db3c0" : "#ddd")};
    box-shadow: ${(props) =>
      props.isOpen ? "0 0 0 3px rgba(45, 179, 192, 0.2)" : "none"};
  }
`;

const DropdownIcon = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%)
    ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  font-size: 10px;
  color: #666;
  pointer-events: none;
  z-index: 3;
  transition: transform 0.2s ease;
`;

const DropdownMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #2db3c0;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 250px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: dropdownSlideUp 0.2s ease-out;

  @keyframes dropdownSlideUp {
    from {
      opacity: 0;
      transform: translateY(8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const OptionsList = styled.div`
  max-height: 250px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }
`;

const OptionItem = styled.div`
  padding: 10px 14px;
  cursor: pointer;
  font-family: "Futura PT", sans-serif;
  font-size: 14px;
  background-color: ${(props) => (props.selected ? "#e6f7f9" : "white")};
  color: ${(props) => (props.selected ? "#2db3c0" : "#333")};
  font-weight: ${(props) => (props.selected ? "500" : "normal")};
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.15s ease;

  &:hover {
    background-color: ${(props) => (props.selected ? "#d0f0f5" : "#f8f9fa")};
    color: ${(props) => (props.selected ? "#2db3c0" : "#2db3c0")};
  }

  &:active {
    background-color: #c0eef3;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NoResults = styled.div`
  padding: 16px 14px;
  text-align: center;
  color: #999;
  font-family: "Futura PT", sans-serif;
  font-size: 14px;
  font-style: italic;
`;

const ErrorText = styled.span`
  color: #e74c3c;
  font-size: 13px;
  margin-top: 4px;
  display: block;
`;

const SubmitError = styled.div`
  background-color: #fee;
  color: #c33;
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
  text-align: center;
  border: 1px solid #fcc;
`;

const ApiResponseMessage = styled.div`
  background-color: ${(props) => (props.success ? "#d4edda" : "#f8d7da")};
  color: ${(props) => (props.success ? "#155724" : "#721c24")};
  padding: 12px;
  border-radius: 4px;
  margin-bottom: 16px;
  font-size: 14px;
  text-align: center;
  border: 1px solid ${(props) => (props.success ? "#c3e6cb" : "#f5c6cb")};
  animation: fadeIn 0.3s ease-out;
`;

const Policy = styled.div`
  background-color: #eaeaea;
  padding: 5px;
  text-align: center;
`;
